package com.tovtalk.presentation.viewmodel.search

import com.tovtalk.presentation.ViewModel
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.RecentSearchesRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.domain.repository.HashtagRepository
import com.tovtalk.domain.repository.HashtagInfo
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

/**
 * ViewModel for Search Screen
 */
@OptIn(FlowPreview::class)
class SearchViewModel(
    private val userRepository: UserRepository,
    private val recentSearchesRepository: RecentSearchesRepository,
    private val hashtagRepository: HashtagRepository,
    private val scope: CoroutineScope
) : ViewModel() {

    private val _searchQuery = MutableStateFlow("")
    val searchQuery: StateFlow<String> = _searchQuery.asStateFlow()

    private val _searchResults = MutableStateFlow<UiState<List<User>>>(UiState.Idle)
    val searchResults: StateFlow<UiState<List<User>>> = _searchResults.asStateFlow()

    private val _recentSearches = MutableStateFlow<List<String>>(emptyList())
    val recentSearches: StateFlow<List<String>> = _recentSearches.asStateFlow()

    private val _suggestedArtists = MutableStateFlow<UiState<List<User>>>(UiState.Idle)
    val suggestedArtists: StateFlow<UiState<List<User>>> = _suggestedArtists.asStateFlow()

    private val _suggestedArtistsHasMore = MutableStateFlow(false)
    val suggestedArtistsHasMore: StateFlow<Boolean> = _suggestedArtistsHasMore.asStateFlow()

    private val _suggestedArtistsLoadingMore = MutableStateFlow(false)
    val suggestedArtistsLoadingMore: StateFlow<Boolean> = _suggestedArtistsLoadingMore.asStateFlow()

    private val _followLoadingUserId = MutableStateFlow<Int?>(null)
    val followLoadingUserId: StateFlow<Int?> = _followLoadingUserId.asStateFlow()

    private val _trendingHashtags = MutableStateFlow<UiState<List<HashtagInfo>>>(UiState.Idle)
    val trendingHashtags: StateFlow<UiState<List<HashtagInfo>>> = _trendingHashtags

    private val _hashtagSuggestions = MutableStateFlow<List<HashtagInfo>>(emptyList())
    val hashtagSuggestions: StateFlow<List<HashtagInfo>> = _hashtagSuggestions

    private val _hashtagPosts = MutableStateFlow<UiState<List<Post>>>(UiState.Idle)
    val hashtagPosts: StateFlow<UiState<List<Post>>> = _hashtagPosts

    private var hashtagPostsCursor: String? = null
    private var hashtagPostsLoading = false

    // Drives the hashtag-posts search. flatMapLatest cancels a superseded search so
    // a slow result from an old query can never append onto a newer query's list.
    private val hashtagPostsTag = MutableStateFlow<String?>(null)
    // The tag whose results currently populate _hashtagPosts. loadMore only appends
    // when its captured tag still matches this, preventing stale cross-query appends.
    private var activeHashtagTag: String? = null

    init {
        loadRecentSearches()
        loadSuggestedArtists()
        loadTrendingHashtags()
        setupHashtagPostsSearch()
        scope.launch {
            _searchQuery
                .debounce(300)
                .distinctUntilChanged()
                .collect { query ->
                    if (query.isBlank()) {
                        _searchResults.value = UiState.Idle
                        _hashtagSuggestions.value = emptyList()
                        searchHashtagPosts("")
                        loadSuggestedArtists()
                    } else if (query.startsWith("#") && query.length > 1) {
                        _suggestedArtists.value = UiState.Idle
                        _searchResults.value = UiState.Idle
                        val tag = query.removePrefix("#")
                        searchHashtagAutocomplete(tag)
                        searchHashtagPosts(tag)
                    } else {
                        _suggestedArtists.value = UiState.Idle
                        searchHashtagPosts("")
                        _hashtagSuggestions.value = emptyList()
                        searchUsers(query)
                    }
                }
        }
    }

    private fun loadRecentSearches() {
        _recentSearches.value = recentSearchesRepository.getRecentSearches()
    }

    fun updateSearchQuery(query: String) {
        _searchQuery.value = query
    }

    private fun searchUsers(query: String) {
        scope.launch {
            _searchResults.value = UiState.Loading

            when (val result = userRepository.searchUsers(query, page = 0)) {
                is DomainResult.Success -> {
                    _searchResults.value = UiState.Success(result.data)
                    recentSearchesRepository.addRecentSearch(query)
                    loadRecentSearches()
                }
                is DomainResult.Error -> {
                    _searchResults.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    private fun loadTrendingHashtags() {
        scope.launch {
            _trendingHashtags.value = UiState.Loading
            when (val result = hashtagRepository.getTrending()) {
                is DomainResult.Success -> _trendingHashtags.value = UiState.Success(result.data)
                is DomainResult.Error -> _trendingHashtags.value = UiState.Error(result.message)
                else -> {}
            }
        }
    }

    private fun searchHashtagAutocomplete(query: String) {
        scope.launch {
            when (val result = hashtagRepository.searchHashtags(query)) {
                is DomainResult.Success -> _hashtagSuggestions.value = result.data
                is DomainResult.Error -> _hashtagSuggestions.value = emptyList()
                else -> {}
            }
        }
    }

    private fun searchHashtagPosts(tag: String) {
        // Just publish the tag; the flatMapLatest pipeline in setupHashtagPostsSearch
        // performs the fetch and cancels any in-flight search for a previous tag.
        hashtagPostsTag.value = tag
    }

    @OptIn(kotlinx.coroutines.ExperimentalCoroutinesApi::class)
    private fun setupHashtagPostsSearch() {
        scope.launch {
            hashtagPostsTag
                .flatMapLatest { tag ->
                    flow {
                        if (tag.isNullOrBlank()) {
                            activeHashtagTag = null
                            hashtagPostsCursor = null
                            emit(UiState.Idle)
                        } else {
                            emit(UiState.Loading)
                            when (val result = hashtagRepository.getPostsByHashtag(tag)) {
                                is DomainResult.Success -> {
                                    // First page for this tag is now the active one.
                                    activeHashtagTag = tag
                                    hashtagPostsCursor = result.data.second
                                    emit(UiState.Success(result.data.first))
                                }
                                is DomainResult.Error -> emit(UiState.Error(result.message))
                                else -> {}
                            }
                        }
                    }
                }
                .collect { _hashtagPosts.value = it }
        }
    }

    fun loadMoreHashtagPosts() {
        if (hashtagPostsLoading || hashtagPostsCursor == null) return
        val tag = activeHashtagTag ?: return
        if (tag.isBlank()) return
        val cursor = hashtagPostsCursor
        hashtagPostsLoading = true
        scope.launch {
            when (val result = hashtagRepository.getPostsByHashtag(tag, cursor = cursor)) {
                is DomainResult.Success -> {
                    // Guard against a query switch that happened while this page was in flight:
                    // only append if the active tag is still the one we paged for.
                    if (activeHashtagTag == tag) {
                        val existing = (_hashtagPosts.value as? UiState.Success)?.data ?: emptyList()
                        hashtagPostsCursor = result.data.second
                        _hashtagPosts.value = UiState.Success(existing + result.data.first)
                    }
                }
                is DomainResult.Error -> {}
                else -> {}
            }
            hashtagPostsLoading = false
        }
    }

    fun searchByHashtag(tag: String) {
        _searchQuery.value = "#$tag"
    }

    fun selectRecentSearch(query: String) {
        _searchQuery.value = query
        searchUsers(query)
    }

    fun clearRecentSearches() {
        recentSearchesRepository.clearRecentSearches()
        loadRecentSearches()
    }

    fun clearSearch() {
        _searchQuery.value = ""
        _searchResults.value = UiState.Idle
        _hashtagSuggestions.value = emptyList()
        searchHashtagPosts("")
        loadSuggestedArtists()
    }

    fun loadSuggestedArtists() {
        scope.launch {
            _suggestedArtists.value = UiState.Loading
            when (val result = userRepository.getSuggestedArtists(limit = 20, offset = 0)) {
                is DomainResult.Success -> {
                    _suggestedArtists.value = UiState.Success(result.data.list)
                    _suggestedArtistsHasMore.value = result.data.hasMore
                }
                is DomainResult.Error -> {
                    _suggestedArtists.value = UiState.Error(result.message)
                    _suggestedArtistsHasMore.value = false
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    fun loadMoreSuggestedArtists() {
        scope.launch {
            val current = _suggestedArtists.value
            val list = (current as? UiState.Success)?.data ?: return@launch
            if (_suggestedArtistsLoadingMore.value || !_suggestedArtistsHasMore.value) return@launch

            _suggestedArtistsLoadingMore.value = true
            when (val result = userRepository.getSuggestedArtists(limit = 20, offset = list.size)) {
                is DomainResult.Success -> {
                    val newList = list + result.data.list
                    _suggestedArtists.value = UiState.Success(newList)
                    _suggestedArtistsHasMore.value = result.data.hasMore
                }
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
            _suggestedArtistsLoadingMore.value = false
        }
    }

    fun followUser(userId: Int) {
        scope.launch {
            _followLoadingUserId.value = userId
            when (val result = userRepository.followUser(userId)) {
                is DomainResult.Success -> {
                    updateUserFollowingState(userId, true)
                }
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
            _followLoadingUserId.value = null
        }
    }

    private fun updateUserFollowingState(userId: Int, isFollowing: Boolean) {
        val updated = { list: List<User> ->
            list.map { if (it.id == userId) it.copy(isFollowing = isFollowing) else it }
        }
        when (val s = _searchResults.value) {
            is UiState.Success -> _searchResults.value = UiState.Success(updated(s.data))
            else -> {}
        }
        when (val s = _suggestedArtists.value) {
            is UiState.Success -> _suggestedArtists.value = UiState.Success(updated(s.data))
            else -> {}
        }
    }
}
